import { useContext } from 'react';
import {
    GelRowLayout,
    GelBoxLayout,
    GelScreenDetectorContext,
    GelParagraph
} from '@tal-gel/components';
import { getGelTokens } from '@tal-gel/theming';
import { HorizontalSeperator, VerticalSeperator } from './dividers';
import { useGetClaimDetailsContent } from '../../../common/api/graphQLDataFetch';
import { ClaimDetails } from '../../../common/models/memberclaims';
import { formatCurrency, Spacer, StyledGelLabel } from './common';

interface ClaimConsultantDetailsProps {
    caseManager: string;
}
const ClaimConsultantDetails = ({caseManager}:ClaimConsultantDetailsProps) => {
    const { global: { sizeBaseX5 } } = getGelTokens()
    const { data } = useGetClaimDetailsContent()

    return (
        <GelRowLayout style={{ paddingBottom: sizeBaseX5, minWidth: sizeBaseX5 * 5 }} gutter="small">
            <GelBoxLayout alignment="start">
                <StyledGelLabel small id={"caseManagerLabel"}>{data?.policyInformation?.CaseManager}</StyledGelLabel>
                <GelParagraph small id={"caseManagerValue"} style={{ display: "flex", justifyContent: "end", fontWeight: "400" }}>
                    {caseManager ?? data?.policyInformation?.DefaultCaseManager}
                </GelParagraph>
            </GelBoxLayout>
        </GelRowLayout>
    )
}

interface IpBenefitDetailsProps {
    details: ClaimDetails;
}
const IpBenefitDetails = ({details: {monthlyBenefit, claimSCWaitingPeriod, claimSCBenefitPeriod}}:IpBenefitDetailsProps) => {
    const { global: { sizeBaseX5 } } = getGelTokens()
    const { data } = useGetClaimDetailsContent()

    return (
        <GelRowLayout style={{ paddingBottom: sizeBaseX5, minWidth: sizeBaseX5 * 5 }} gutter="small">
            <GelBoxLayout>
                <StyledGelLabel small id={"benefitAmountLabel"}>{data?.policyInformation?.BenefitAmount}</StyledGelLabel>
                <GelParagraph small id={"benefitAmountValue"} style={{ display: "flex", justifyContent: "end", fontWeight: "400" }}>
                    {formatCurrency(monthlyBenefit)}
                </GelParagraph>
            </GelBoxLayout>
            <GelBoxLayout alignmentIndividual={['center', 'end']}>
                <StyledGelLabel small id={"waitingPeriodLabel"} >{data?.policyInformation?.WaitingPeriod}</StyledGelLabel>
                <GelParagraph small id={"waitingPeriodValue"} style={{ display: "flex", justifyContent: "end", fontWeight: "400" }}>
                    {claimSCWaitingPeriod}
                </GelParagraph>
            </GelBoxLayout>
            <GelBoxLayout alignmentIndividual={['center', 'end']}>
                <StyledGelLabel small id={"benefitPeriodLabel"}>{data?.policyInformation?.BenefitPeriod}</StyledGelLabel>
                <GelParagraph small id={"benefitPeriodvalue"} style={{ display: "flex", justifyContent: "end", fontWeight: "400" }}>{claimSCBenefitPeriod}</GelParagraph>
            </GelBoxLayout>
        </GelRowLayout>
    )
}

interface IpPolicyDetailsProps {
    details: ClaimDetails;
}
const IpPolicyDetails = ({details: {policyOwner, masterPolicyNo, lifeInsured}}:IpPolicyDetailsProps) => {
    const { global: { sizeBaseX5 } } = getGelTokens()
    const { data } = useGetClaimDetailsContent()
    
    return (
        <GelRowLayout style={{ paddingBottom: sizeBaseX5, minWidth: sizeBaseX5 * 5 }} gutter="small">
            <GelBoxLayout alignment="start">
                <StyledGelLabel small id={"policyOwnerLabel"}>{data?.policyInformation?.PolicyOwner}</StyledGelLabel>
                <GelParagraph small id={"policyOwnerValue"} style={{ display: "flex", textAlign: "end", justifyContent: "end", fontWeight: "400" }}>
                    {policyOwner}
                </GelParagraph>
            </GelBoxLayout>
            <GelBoxLayout alignment="start">
                <StyledGelLabel small id={"policyNumberLabel"}>{data?.policyInformation?.PolicyNumber}</StyledGelLabel>
                <GelParagraph small id={"policyNumberValue"} style={{ display: "flex", justifyContent: "end", fontWeight: "400" }}>
                    {masterPolicyNo}
                </GelParagraph>
            </GelBoxLayout>
            <GelBoxLayout alignment="start">
                <StyledGelLabel small id={"lifeInsuredLabel"}>{data?.policyInformation?.LifeInsured}</StyledGelLabel>
                <GelParagraph small id={"lifeInsuredValue"} style={{ display: "flex", justifyContent: "end", fontWeight: "400", textAlign: "right" }}>
                    {lifeInsured}
                </GelParagraph>
            </GelBoxLayout>
        </GelRowLayout>
    )
}

interface IPDetailsProps {
    details: ClaimDetails;
    caseManager: string;
}
const IPDetails = ({details, caseManager}: IPDetailsProps) => {
    const { screen, isXsScreen, isSmScreen, isLargerThanLgScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;
    const { global: { sizeBaseUnit } } = getGelTokens()

    if (isXsScreen(screen) | isSmScreen(screen)) {
        return (
            <GelRowLayout>
                <IpPolicyDetails details={details} />
                <HorizontalSeperator />
                <Spacer />
                <IpBenefitDetails details={details} />
                <HorizontalSeperator />
                <Spacer />
                <ClaimConsultantDetails caseManager={caseManager} />
            </GelRowLayout>
        );
    }
    else {
        return (
            <GelBoxLayout space={[5, 0.5, 5, 0.5, 5]} alignment="start">
                <IpPolicyDetails details={details} />
                <VerticalSeperator style={{ height: !isLargerThanLgScreen(screen) ? sizeBaseUnit * 45 : sizeBaseUnit * 30 }} />
                <IpBenefitDetails details={details} />
                <VerticalSeperator style={{ height: !isLargerThanLgScreen(screen) ? sizeBaseUnit * 45 : sizeBaseUnit * 30 }} />
                <ClaimConsultantDetails caseManager={caseManager} />
            </GelBoxLayout>
        );
    }
};

export default IPDetails;